import {
  Cart,
  CartItemInterface,
  ConfigurableProductOptions,
  ConfigurableVariant,
  Maybe,
  ProductInterface,
  SelectedConfigurableOption
} from "@/types/graphql.prod";
import {
  findMatchingVariant
} from "@/util/findMatchingProductVariant";
import {
  isProductConfigurable
} from "@/util/isProductConfigurable";

export type ExtentProductVariants = ProductInterface & {
  variants?: Maybe<Array<Maybe<ConfigurableVariant>>>;
  configurable_options?: Maybe<Array<Maybe<ConfigurableProductOptions>>>;
}

// 1. 首先扩展 CartItemInterface
export type ExtendedCartItemInterface = Omit<CartItemInterface, "product"> & {
  configurable_options?: Maybe<Array<Maybe<SelectedConfigurableOption>>>;
  product: ExtentProductVariants
};

// 2. 然后基于 Cart 类型创建新类型，只替换 items 属性
export type ExtendedCart = Omit<Cart, "items"> & {
  items?: Maybe<Array<Maybe<ExtendedCartItemInterface>>>;
};


const OUT_OF_STOCK_CODE = "OUT_OF_STOCK"

function deriveOptionSelectionsFromProduct(cartItem: Maybe<ExtendedCartItemInterface>): Map<string, number> {
  const initialOptionSelections = new Map<string, number>();

  if (!cartItem) {
    return initialOptionSelections;
  }

  const configurable_options = cartItem.configurable_options;


  if (!configurable_options || configurable_options.length === 0) {
    return initialOptionSelections;
  }


  for (const option of configurable_options) {
    if (isValidConfigurableOption(option)) {
      initialOptionSelections.set(String(option.id), option.value_id);
    }
  }

  return initialOptionSelections;
}

function isValidConfigurableOption(option: Maybe<SelectedConfigurableOption>): option is SelectedConfigurableOption {
  return (
    option !== null &&
    option !== undefined &&
    typeof option.id === "number" &&
    typeof option.value_id === "number"
  );
}

function isValidConfigurableOption2(option: Maybe<ConfigurableProductOptions>): option is ConfigurableProductOptions {
  return (
    option !== null &&
    option !== undefined &&
    typeof option.attribute_id === "string" &&
    typeof option.attribute_code === "string"
  );
}

function deriveOptionCodesFromProduct(product: ExtentProductVariants | undefined) {
  const INITIAL_OPTION_CODES = new Map<Maybe<string> | undefined, Maybe<string> | undefined>()

  if (!product) {
    return INITIAL_OPTION_CODES
  }

  // If this is a simple product it has no option codes.
  if (!isProductConfigurable(product)) {
    return INITIAL_OPTION_CODES
  }

  // Initialize optionCodes based on the options of the product.
  const initialOptionCodes = new Map<Maybe<string> | undefined, Maybe<string> | undefined>()

  const configurable_options = product?.configurable_options;


  if (!configurable_options || configurable_options.length === 0) {
    return initialOptionCodes;
  }


  for (const option of configurable_options) {
    if (isValidConfigurableOption2(option)) {
      initialOptionCodes.set(option?.attribute_id, option?.attribute_code);
    }
  }


  return initialOptionCodes
}

const getIsOutOfStock = (product: ExtentProductVariants | undefined, optionCodes: Map<Maybe<string> | undefined, Maybe<string> | undefined>, optionSelections: Map<string, number>) => {

  if (!product) { return false }

  const {
    stock_status, variants 
  } = product

  const isConfigurable = isProductConfigurable(product)

  const optionSelectionsArrray = Array.from(optionSelections.values())
  const optionsSelectedLength = optionSelectionsArrray.filter((value) => {
    return !!value
  }).length

  let optionsSelected = false

  if (optionsSelectedLength >= optionSelectionsArrray.length) {
    optionsSelected = true
  }

  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    })

    const {
      product 
    } = item || {}

    const {
      presale_product 
    } = product || {}

    const {
      status: isPreOrder 
    } = presale_product || {}

    if (isPreOrder) {
      return false
    }

    const stockStatus = item?.product?.stock_status


    return stockStatus === OUT_OF_STOCK_CODE || !stockStatus
  }

  return stock_status === OUT_OF_STOCK_CODE
}

export const isCartProductOutOfStock = (product, selectedConfigurableOptions) => {
  if (!product) { return false; }

  if (product.__typename === "ConfigurableProduct") {
    const variants = product.variants;

    if (
      !Array.isArray(selectedConfigurableOptions) ||
      selectedConfigurableOptions.length === 0 ||
      !Array.isArray(variants) ||
      variants.length === 0
    ) {
      return false;
    }


    const matchedVariantProduct = variants.find(variant => {
      const attributes = variant?.attributes;

      return !!selectedConfigurableOptions.every(selectedOption => {
        return !!attributes?.some(attr => !!attr && attr?.uid === selectedOption?.configurable_product_option_value_uid,);
      });
    })?.product;


    return matchedVariantProduct
      ? matchedVariantProduct.stock_status === "OUT_OF_STOCK" && !matchedVariantProduct.presale_product?.status
      : false;
  }

  if (product.__typename === "SimpleProduct") {
    return product.stock_status === "OUT_OF_STOCK" && !product.presale_product?.status;
  }

  return false;
};


export const getCartItemIsOutOfStock = ({
  item
}:{
  item: Maybe<ExtendedCartItemInterface>
}) => {
  // const product: ExtentProductVariants | undefined = item?.product

  // const optionSelections = deriveOptionSelectionsFromProduct(item)

  // const optionCodes = deriveOptionCodesFromProduct(product)

  // const isOutOfStock = getIsOutOfStock(product, optionCodes, optionSelections)

  const isOutOfStock = isCartProductOutOfStock(item?.product, item?.configurable_options)

  return {
    isOutOfStock
  }
}

interface CheckCartStockResult {
  hasOutOfStock: boolean; // 是否有缺货商品
  outOfStockItems: ExtendedCartItemInterface[]; // 缺货商品列表
}

// 判断购物车中是否有缺货的商品
export const checkCartItemsStock = (items: Maybe<Array<Maybe<ExtendedCartItemInterface>>>): CheckCartStockResult => {
  // 如果没有商品，返回默认值
  if (!items || items.length === 0) {
    return {
      hasOutOfStock: false,
      outOfStockItems: []
    };
  }

  const outOfStockItems: ExtendedCartItemInterface[] = [];

  // 遍历所有商品检查库存状态
  items.forEach(item => {
    if (item) {
      const {
        isOutOfStock 
      } = getCartItemIsOutOfStock({
        item 
      });
      
      if (isOutOfStock) {
        outOfStockItems.push(item);
      }
    }
  });

  return {
    hasOutOfStock: outOfStockItems.length > 0,
    outOfStockItems
  };
};
